/**
 * Form helper / QR‑code / Receipt utilities
 *
 * This single file centralises a lot of cross‑cutting helpers used by the
 *   ‑ QR‑code subsystem (ticket, receipt, badge)
 *   ‑ location + timestamp formatting
 *   ‑ dynamic ZPL (Zebra Printer Language) receipt generation
 *   ‑ toast creation, base‑64 helpers, image watermarking
 *   ‑ FormDefinition traversal utilities
 */

import { AbstractControl, AbstractControlDirective, UntypedFormControl } from '@angular/forms';
import * as dayjs from 'dayjs';
import { GlobalConstants } from '../constant/constant';
import { FORM_BUILDER_CONSTANTS, FORM_CONTROL_TYPES, QR_CODE_TYPES } from '../enums/general.enum';
import FormControl from '../models/form-definition/form-control';
import FormDefinition from '../models/form-definition/form-definition';

/**
 * Generates the modal message text to display when showing a QR code.
 * For Driver App QR codes, prefers `driverAppQrCode.modalMessage`, then falls
 * back to the legacy `qrCodeModalMessages.receipt` field.
 */
export const generateQrCodeModalText = (formDefinition: FormDefinition, type: QR_CODE_TYPES): string => {

  if (type === QR_CODE_TYPES.SHARED_FORM) {
    return formDefinition.qrCodeModalMessages?.ticket || '';
  } else {
    return (
      formDefinition.driverAppQrCodeConfig?.modalMessage ||
      formDefinition.qrCodeModalMessages?.receipt ||
      ''
    );
  }
};

/**
 * Converts a Blob object to a base64 string.
 * @param blob The Blob object to convert.
 * @returns A promise that resolves to the base64 string.
 */
export const convertBlobToBase64 = async (blob: Blob): Promise<string> => {
  const reader = getFileReader();
  return new Promise((resolve, reject) => {
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Adds a watermark to a badge photo.
 * @param blob The Blob object representing the badge photo.
 * @returns A promise that resolves to the base64 string of the updated image with the watermark.
 */
export const addWatermarkToBadgePhoto = async (blob: Blob): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    const img = new Image();
    const base64Image = await convertBlobToBase64(blob);
    img.src = base64Image;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;

      ctx.drawImage(img, 0, 0);
      const text = 'UNVERIFIED';
      const maxWidth = canvas.width;
      const maxHeight = canvas.height;
      let fontSize = 1;
      ctx.font = fontSize + 'px Arial';
      while (ctx.measureText(text).width < maxWidth && fontSize < maxHeight) {
        if (ctx.measureText(text).width + fontSize < maxWidth && fontSize < maxHeight) {
          fontSize++;
          ctx.font = fontSize + 'px Arial';
        } else {
          break;
        }
      }
      ctx.fillStyle = '#FF0000';
      const x = (canvas.width - ctx.measureText(text).width) / 2;
      const y = canvas.height - 20;
      ctx.fillText(text, x, y);

      const updatedBase64 = canvas.toDataURL('image/jpeg');
      resolve(updatedBase64);
    };

    img.onerror = function (error) {
      console.log('watermark error', error);
      reject(error);
    };
  });
};

/**
 * Checks if a control is required based on its validators.
 * @param ngControl The control to check.
 * @param isGroupControl Indicates whether the control is part of a group.
 * @returns True if the control is required, false otherwise.
 */
export const isControlRequired = (ngControl: AbstractControl | AbstractControlDirective, isGroupControl = false) => {
  let formControl = ngControl;
  let hasRequiredValidator = false;

  if (isGroupControl) {
    formControl = (ngControl as UntypedFormControl).get(FORM_BUILDER_CONSTANTS.ITEMS);
  }

  if (formControl?.validator) {
    const validator = formControl.validator({} as AbstractControl);
    if (validator && validator.required) {
      hasRequiredValidator = true;
    }
  }

  return hasRequiredValidator;
};

/**
 * Validates if the badge timestamp is within the specified interval.
 * @param badgeDatetime The timestamp of the badge.
 * @param interval The interval in milliseconds to check against.
 * @returns True if the badge timestamp is within the interval, false otherwise.
 */
export const validateBadgeTimestamp = (badgeDatetime: number, interval: number) => {
  const currentDate = new Date();
  const maxDatetime = Math.floor((currentDate.getTime() - interval) / 1000);

  return dayjs(maxDatetime).isBefore(dayjs(badgeDatetime));
};

/**
 * Extracts shared controls from the form definition.
 * @param formDefinition The form definition to extract shared controls from.
 * @returns An array of shared controls.
 */
export const extractSharedControls = (formDefinition: FormDefinition) => {
  const sharedControls: FormControl[] = [];

  for (const step of formDefinition.steps) {
    for (const control of step.controls) {
      if (control.shared) {
        sharedControls.push(control);
      }
    }
  }

  return sharedControls;
};

export const isControlArrayType = (inputType: string, inputMode?: string | number) => {
  if (inputMode) {
    return GlobalConstants.ARRAY_CONTROLS.includes(inputType) || GlobalConstants.ARRAY_CONTROLS.includes(inputMode.toString());
  } else {
    return GlobalConstants.ARRAY_CONTROLS.includes(inputType);
  }
};

/**
 * Retrieves all controls from the form definition, including nested group controls.
 * @param formDefinition The form definition to extract controls from.
 * @returns An array of all controls in the form definition.
 */
export const getAllControlsFromFormDefinition = (
  formDefinition: FormDefinition
): FormControl[] => {
  const findControls = (
    controls: FormControl[],
    outputList: FormControl[] = []
  ) => {
    controls.forEach((control) => {
      if (control.type.inputType === FORM_CONTROL_TYPES.GROUP) {
        outputList.push(control);
        control.type.parentControls?.forEach((parentControl) => outputList.push(parentControl));
        findControls(control.type.groupControls || [], outputList);
      } else {
        outputList.push(control);
      }
    });
    return outputList;
  };

  const controls: FormControl[] = [];
  formDefinition.steps.forEach((step) => {
    findControls(step.controls, controls);
  });

  return controls;
};


const getFileReader = (): FileReader => {
  const fileReader = new FileReader();
  const zoneOriginialInstance = (fileReader as any)['__zone_symbol__originalInstance'];
  return zoneOriginialInstance || fileReader;
};

/**
 * Inserts indexes into the path string based on the provided paths.
 * @param path1 The first path string.
 * @param path2 The second path string.
 * @returns The updated path string with indexes inserted.
 */
export const insertIndexesIntoPath = (path1: string, path2: string): string => {
  const path1Parts = path1.split('.');
  const path2Parts = path2.split('.');

  // Start building the updated path
  let path2Updated = '';
  let path1Index = 0; // Pointer to track the position in path1

  for (let i = 0; i < path2Parts.length; i++) {
    // Handle 'items' and insert the correct index
    if (path2Parts[i] === FORM_BUILDER_CONSTANTS.ITEMS) {
      const nextPart = path2Parts[i + 1]; // Check if there is an index after 'items'
      const path1ItemsIndex = path1Parts.indexOf(FORM_BUILDER_CONSTANTS.ITEMS, path1Index); // Find 'items' in path1

      if (path1ItemsIndex !== -1 && nextPart) {
        // If 'items' in path2 has a specific index, append it from path1
        const path1IndexVal = path1Parts[path1ItemsIndex + 1];
        path2Updated += `${FORM_BUILDER_CONSTANTS.ITEMS}.${path1IndexVal}.`;
        path1Index = path1ItemsIndex + 2;
      } else {
        // If no specific index after 'items', simply append 'items'
        path2Updated += `${FORM_BUILDER_CONSTANTS.ITEMS}.`;
      }
    } else {
      // Append normal path parts
      path2Updated += `${path2Parts[i]}.`;
    }
  }

  // Remove the trailing dot if it exists
  return path2Updated.endsWith('.') ? path2Updated.slice(0, -1) : path2Updated;
}

export const getFormControlData = (formDefinition: FormDefinition, controlKey: string) => {
  for (const step of formDefinition.steps) {
    for (const control of step.controls) {
      if (control.key === controlKey) {
        return { step, control, path: `${step.key}.${control.key}` };
      }
    }
  }

  return null;
};
